import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { listGoogleAccounts, type GoogleAccount } from '../../services/google-account-api.js';
import {
  listIntegrationAccounts,
  upsertIntegrationAccount,
  deleteIntegrationAccount,
  type IntegrationAccount,
} from '../../services/integration-account-api.js';

const REQUIRED_SCOPE = 'https://www.googleapis.com/auth/adwords';

@customElement('cp-google-ads-settings')
export class CpGoogleAdsSettings extends LitElement {
  createRenderRoot() { return this; }

  @state() private _googleAccounts: GoogleAccount[] = [];
  @state() private _integration: IntegrationAccount | null = null;
  @state() private _selectedAccountId = 0;
  @state() private _loading = true;
  @state() private _error = '';

  connectedCallback(): void {
    super.connectedCallback();
    this._load();
  }

  private async _load(): Promise<void> {
    this._loading = true;
    try {
      const [googleAccounts, integrations] = await Promise.all([
        listGoogleAccounts(),
        listIntegrationAccounts(),
      ]);
      this._googleAccounts = googleAccounts;
      this._integration = integrations.find((i) => i.integration_type === 'google-ads') ?? null;
      this._selectedAccountId = this._integration?.google_account_id ?? 0;
      this._error = '';
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private _hasRequiredScope(account: GoogleAccount): boolean {
    return account.scopes?.includes(REQUIRED_SCOPE) ?? false;
  }

  private async _handleConnect(): Promise<void> {
    if (!this._selectedAccountId) return;
    try {
      await upsertIntegrationAccount('google-ads', {
        google_account_id: this._selectedAccountId,
      });
      await this._load();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Verbinden';
    }
  }

  private async _handleDisconnect(): Promise<void> {
    if (!confirm('Google Ads Integration trennen?')) return;
    try {
      await deleteIntegrationAccount('google-ads');
      await this._load();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Trennen';
    }
  }

  render() {
    if (this._loading) return html`<div class="cp-integration"><p class="cp-integration__empty">Laden...</p></div>`;

    const connectedAccount = this._integration
      ? this._googleAccounts.find((ga) => ga.id === this._integration!.google_account_id)
      : null;

    return html`
      <div class="cp-integration">
        <h3 class="cp-integration__title">Google Ads</h3>
        <p class="cp-integration__desc">Verbinde einen Google Account fuer Google Ads Zugriff.</p>

        ${this._error ? html`<p style="color: var(--color-danger, #ff3b30); font-size: var(--font-size-sm);">${this._error}</p>` : nothing}

        ${this._integration && connectedAccount ? html`
          <div class="cp-integration__status">
            <span class="cp-integration__status-dot ${this._hasRequiredScope(connectedAccount) ? 'cp-integration__status-dot--ok' : 'cp-integration__status-dot--warning'}"></span>
            <div class="cp-integration__status-info">
              <div class="cp-integration__status-label">${connectedAccount.account_alias}</div>
              <div class="cp-integration__status-detail">${connectedAccount.google_email}</div>
            </div>
            <div>
              ${!this._hasRequiredScope(connectedAccount) ? html`
                <p class="cp-integration__hint">Fehlende Berechtigung — bitte Account unter Google Accounts erneut verbinden.</p>
              ` : nothing}
              <button class="cp-integration__btn cp-integration__btn--danger" @click=${this._handleDisconnect}>Trennen</button>
            </div>
          </div>
        ` : html`
          <div class="cp-integration__form">
            ${this._googleAccounts.length === 0 ? html`
              <p class="cp-integration__hint">Zuerst einen Google Account unter "Google Accounts" verbinden.</p>
            ` : html`
              <div class="cp-integration__field">
                <label class="cp-integration__label">Google Account</label>
                <select class="cp-integration__select"
                  .value=${String(this._selectedAccountId)}
                  @change=${(e: Event) => { this._selectedAccountId = Number((e.target as HTMLSelectElement).value); }}>
                  <option value="0">Account auswaehlen...</option>
                  ${this._googleAccounts.map((ga) => html`
                    <option value=${ga.id}>
                      ${ga.account_alias} (${ga.google_email})
                      ${!this._hasRequiredScope(ga) ? ' — fehlende Scopes' : ''}
                    </option>
                  `)}
                </select>
              </div>
              <div class="cp-integration__actions">
                <button class="cp-integration__btn cp-integration__btn--primary"
                  ?disabled=${!this._selectedAccountId}
                  @click=${this._handleConnect}>Verbinden</button>
              </div>
            `}
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-google-ads-settings': CpGoogleAdsSettings;
  }
}
